import React, { useState, useEffect } from 'react';
import { BASE_URL } from './constant';
import './RegistrationData.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function RegistrationData() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
    hasNext: false,
    hasPrev: false
  });
  const [stats, setStats] = useState({
    overview: { total: 0, halls: 0, portfolios: 0, preferdays: 0 },
    portfolios: [],
    halls: [],
    preferDays: []
  });
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('rca_api_key') || '');
  const [showApiPrompt, setShowApiPrompt] = useState(!sessionStorage.getItem('rca_api_key'));
  const [settingsForm, setSettingsForm] = useState({
    venue: '',
    startTime: '17:30',
    endTime: '20:00',
    day1Label: '',
    day1Datetime: '',
    day1Option: '',
    day1OptionLabel: '',
    day2Label: '',
    day2Datetime: '',
    day2Option: '',
    day2OptionLabel: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const toLocalInputValue = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fetchSettings = async (key) => {
    try {
      const response = await fetch(`${BASE_URL}/selection-settings`, {
        method: 'GET',
        headers: {
          'x-api-key': key,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch selection settings');
      const data = await response.json();
      const s = data.settings;
      if (!s) return;
      setSettingsForm({
        venue: s.venue || '',
        startTime: s.startTime || '17:30',
        endTime: s.endTime || '20:00',
        day1Label: s.days?.[0]?.label || '',
        day1Datetime: toLocalInputValue(s.days?.[0]?.datetime),
        day1Option: s.days?.[0]?.optionValue || '',
        day1OptionLabel: s.days?.[0]?.optionLabel || '',
        day2Label: s.days?.[1]?.label || '',
        day2Datetime: toLocalInputValue(s.days?.[1]?.datetime),
        day2Option: s.days?.[1]?.optionValue || '',
        day2OptionLabel: s.days?.[1]?.optionLabel || ''
      });
    } catch (error) {
      toast.error('Failed to fetch selection settings', {
        position: 'top-center',
        autoClose: 4000
      });
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        venue: settingsForm.venue,
        startTime: settingsForm.startTime,
        endTime: settingsForm.endTime,
        day1Label: settingsForm.day1Label,
        day1Datetime: new Date(settingsForm.day1Datetime).toISOString(),
        day1Option: settingsForm.day1Option,
        day1OptionLabel: settingsForm.day1OptionLabel,
        day2Label: settingsForm.day2Label,
        day2Datetime: new Date(settingsForm.day2Datetime).toISOString(),
        day2Option: settingsForm.day2Option,
        day2OptionLabel: settingsForm.day2OptionLabel
      };
      const response = await fetch(`${BASE_URL}/selection-settings`, {
        method: 'PUT',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data.status === 'Error') {
        throw new Error(data.message || 'Failed to update settings');
      }
      toast.success('Selection settings updated. Homepage will use these values.', {
        position: 'top-center',
        autoClose: 3000
      });
      if (data.settings) {
        setSettingsForm({
          venue: data.settings.venue || '',
          startTime: data.settings.startTime || '17:30',
          endTime: data.settings.endTime || '20:00',
          day1Label: data.settings.days?.[0]?.label || '',
          day1Datetime: toLocalInputValue(data.settings.days?.[0]?.datetime),
          day1Option: data.settings.days?.[0]?.optionValue || '',
          day1OptionLabel: data.settings.days?.[0]?.optionLabel || '',
          day2Label: data.settings.days?.[1]?.label || '',
          day2Datetime: toLocalInputValue(data.settings.days?.[1]?.datetime),
          day2Option: data.settings.days?.[1]?.optionValue || '',
          day2OptionLabel: data.settings.days?.[1]?.optionLabel || ''
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update settings', {
        position: 'top-center',
        autoClose: 4000
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch registrations
  const fetchData = async (page = 1, limit = 20, key) => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/data?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'x-api-key': key,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setRegistrations(data.registrations || []);
      setPagination({
        page: data.page,
        limit: data.limit,
        total: data.total,
        pages: data.pages,
        hasNext: data.hasNext,
        hasPrev: data.hasPrev
      });
    } catch (error) {
      toast.error('Failed to fetch registration data', {
        position: "top-center",
        autoClose: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async (key) => {
    try {
      const response = await fetch(`${BASE_URL}/stats`, {
        method: 'GET',
        headers: {
          'x-api-key': key,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStats({
        overview: {
          total: data.overview?.total || 0,
          halls: data.overview?.halls || 0,
          portfolios: data.overview?.portfolios || 0,
          preferdays: data.overview?.preferdays || 0
        },
        portfolios: data.portfolios || [],
        halls: data.halls || [],
        preferDays: data.preferDays || []
      });
    } catch (error) {
      toast.error('Failed to fetch stats', {
        position: "top-center",
        autoClose: 4000
      });
    }
  };

  // Export all registrations as CSV
  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${BASE_URL}/data?all=true`, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch all registrations');
      }
      const data = await response.json();
      const allRegs = data.registrations || [];
      if (allRegs.length === 0) {
        toast.info('No registrations to export.', { position: "top-center" });
        return;
      }

      // Prepare CSV
      const headers = [
        'ID', 'Name', 'Roll No', 'Email', 'Hall', 'Contact', 'Portfolio',
        'Preferred Day', 'Involvement', 'Hometown', 'Why RCA', 'Registered At'
      ];
      const rows = allRegs.map(reg => [
        reg.id,
        `"${reg.name}"`,
        `"${reg.rollno}"`,
        `"${reg.email}"`,
        `"${reg.hall}"`,
        `"${reg.contact}"`,
        `"${reg.portfolio}"`,
        `"${reg.prefer_day}"`,
        `"${reg.involvement}"`,
        `"${reg.hometown}"`,
        `"${reg.why_rca}"`,
        `"${new Date(reg.created_at).toLocaleString('en-IN', {
          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })}"`
      ]);
      const csvContent =
        headers.join(',') + '\n' +
        rows.map(row => row.join(',')).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `freshers_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported!', { position: "top-center" });
    } catch (error) {
      toast.error('Failed to export CSV', { position: "top-center" });
    }
  };

  useEffect(() => {
    if (apiKey) {
      fetchData(1, 20, apiKey);
      fetchStats(apiKey);
      fetchSettings(apiKey);
    }
    // eslint-disable-next-line
  }, [apiKey]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchData(newPage, pagination.limit, apiKey);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApiKeySubmit = async (e) => {
    e.preventDefault();
    if (apiKey.trim().length < 10) {
      toast.error('Please enter a valid API key.', { position: "top-center" });
      return;
    }
    // Validate API key before proceeding
    try {
      const res = await fetch(`${BASE_URL}/stats`, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok || data.status === "Error") {
        throw new Error(data.message || "Invalid API key");
      }
      sessionStorage.setItem('rca_api_key', apiKey);
      setShowApiPrompt(false);
    } catch (err) {
      toast.error(err.message || "Invalid API key", { position: "top-center" });
    }
  };

  return (
    <>
      <ToastContainer />
      {showApiPrompt ? (
        <div className="api-key-prompt">
          <form onSubmit={handleApiKeySubmit} className="api-key-form">
            <label htmlFor="api-key-input">Enter API Key to view registrations:</label>
            <input
              id="api-key-input"
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              className="api-key-input"
              autoFocus
              required
            />
            <button type="submit" className="api-key-btn">Submit</button>
          </form>
        </div>
      ) : (
        <div className="registration-data-container">
          <div className="header">
            <h1>Freshers Registrations</h1>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="subtitle">
                Total Registrations: <span className="stat-number">{stats.overview.total}</span>
              </div>
              <button className="export-btn" onClick={handleExportCSV}>
                Export All as CSV
              </button>
            </div>
          </div>

          <div className="selection-settings-card">
            <h2 className="selection-settings-title">Selection Venue &amp; Schedule</h2>
            <p className="selection-settings-help">
              These values power the homepage timers/venue and the preferred-date options on the registration form.
            </p>
            <form className="selection-settings-form" onSubmit={handleSaveSettings}>
              <div className="selection-settings-grid">
                <label>
                  Venue
                  <input name="venue" value={settingsForm.venue} onChange={handleSettingsChange} required />
                </label>
                <label>
                  Start time (HH:MM)
                  <input name="startTime" type="time" value={settingsForm.startTime} onChange={handleSettingsChange} required />
                </label>
                <label>
                  End time (HH:MM)
                  <input name="endTime" type="time" value={settingsForm.endTime} onChange={handleSettingsChange} required />
                </label>
              </div>

              <div className="selection-settings-day">
                <h3>Day 1</h3>
                <div className="selection-settings-grid">
                  <label>
                    Timer label
                    <input name="day1Label" value={settingsForm.day1Label} onChange={handleSettingsChange} placeholder="Day 1 : 29th August" required />
                  </label>
                  <label>
                    Date &amp; time
                    <input name="day1Datetime" type="datetime-local" value={settingsForm.day1Datetime} onChange={handleSettingsChange} required />
                  </label>
                  <label>
                    Form option value
                    <input name="day1Option" value={settingsForm.day1Option} onChange={handleSettingsChange} placeholder="29th August" required />
                  </label>
                  <label>
                    Form option label
                    <input name="day1OptionLabel" value={settingsForm.day1OptionLabel} onChange={handleSettingsChange} placeholder="29th Aug, Friday" required />
                  </label>
                </div>
              </div>

              <div className="selection-settings-day">
                <h3>Day 2</h3>
                <div className="selection-settings-grid">
                  <label>
                    Timer label
                    <input name="day2Label" value={settingsForm.day2Label} onChange={handleSettingsChange} placeholder="Day 2 : 30th August" required />
                  </label>
                  <label>
                    Date &amp; time
                    <input name="day2Datetime" type="datetime-local" value={settingsForm.day2Datetime} onChange={handleSettingsChange} required />
                  </label>
                  <label>
                    Form option value
                    <input name="day2Option" value={settingsForm.day2Option} onChange={handleSettingsChange} placeholder="30th August" required />
                  </label>
                  <label>
                    Form option label
                    <input name="day2OptionLabel" value={settingsForm.day2OptionLabel} onChange={handleSettingsChange} placeholder="30th Aug, Saturday" required />
                  </label>
                </div>
              </div>

              <button type="submit" className="export-btn" disabled={savingSettings}>
                {savingSettings ? 'Saving...' : 'Save Selection Settings'}
              </button>
            </form>
          </div>

          {/* Stats Details */}
          <div className="stats-details">
            <div className="simple-stats-cards">
              <div className="simple-stats-card">
                <div className="simple-stats-title">Portfolios</div>
                <div className="simple-stats-list">
                  {stats.portfolios.length === 0 && <div className="simple-stats-empty">No data</div>}
                  {stats.portfolios.map((p) => (
                    <div className="simple-stats-row" key={p.portfolio}>
                      <span>{p.portfolio}</span>
                      <span className="simple-stats-count">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="simple-stats-card">
                <div className="simple-stats-title">Halls</div>
                <div className="simple-stats-list">
                  {stats.halls.length === 0 && <div className="simple-stats-empty">No data</div>}
                  {stats.halls.map((h) => (
                    <div className="simple-stats-row" key={h.hall}>
                      <span>{h.hall}</span>
                      <span className="simple-stats-count">{h.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="simple-stats-card">
                <div className="simple-stats-title">Preferred Days</div>
                <div className="simple-stats-list">
                  {stats.preferDays.length === 0 && <div className="simple-stats-empty">No data</div>}
                  {stats.preferDays.map((d) => (
                    <div className="simple-stats-row" key={d.prefer_day}>
                      <span>{d.prefer_day}</span>
                      <span className="simple-stats-count">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="registration-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Roll No</th>
                      <th>Email</th>
                      <th>Hall</th>
                      <th>Contact</th>
                      <th>Portfolio</th>
                      <th>Preferred Day</th>
                      <th>Involvement</th>
                      <th>Hometown</th>
                      <th>Why RCA</th>
                      <th>Registered At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.length > 0 ? (
                      registrations.map((registration) => (
                        <tr key={registration.id}>
                          <td>{registration.id}</td>
                          <td>{registration.name}</td>
                          <td>{registration.rollno}</td>
                          <td className="wrap-cell">{registration.email}</td>
                          <td>{registration.hall}</td>
                          <td>{registration.contact}</td>
                          <td>{registration.portfolio}</td>
                          <td>{registration.prefer_day}</td>
                          <td className="expandable-cell">
                            <ExpandableCell text={registration.involvement} limit={60} />
                          </td>
                          <td>{registration.hometown}</td>
                          <td className="expandable-cell">
                            <ExpandableCell text={registration.why_rca} limit={60} />
                          </td>
                          <td>{formatDate(registration.created_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="no-data">No registrations found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrev}
                    className="pagination-btn"
                  >
                    ← Previous
                  </button>

                  <span className="page-counter">
                    Page {pagination.page} of {pagination.pages}
                  </span>

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
              {pagination.pages === 1 && (
                <div className="pagination">
                  <span className="page-counter">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

function ExpandableCell({ text, limit = 60 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > limit;
  return (
    <div
      className={`cell-content${isLong ? ' clickable' : ''}`}
      onClick={() => isLong && setExpanded((v) => !v)}
      title={isLong ? (expanded ? "Show less" : "Show more") : undefined}
      style={{ cursor: isLong ? "pointer" : "default" }}
    >
      <span className="cell-text">
        {expanded || !isLong ? text : text.slice(0, limit) + '...'}
      </span>
      {isLong && (
        <span className="expand-indicator">
          {expanded ? "▲" : "▼"}
        </span>
      )}
    </div>
  );
}

export default RegistrationData;